import { useState, type FormEvent } from 'react';
import { navLinks, noticeTicker } from '@/mocks/home';
import { useToast } from '@/components/base/Toast';

type SiteHeaderProps = {
  onSearch: (keyword: string) => void;
};

export default function SiteHeader({ onSearch }: SiteHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const showToast = useToast();

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const value = keyword.trim();
    onSearch(value);
    const target = document.getElementById('entries');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleGetEntry = () => {
    showToast('已为你定位到最新入口列表');
    const target = document.getElementById('entries');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* top operational bar */}
      <div className="bg-foreground-950 text-background-100">
        <div className="mx-auto flex h-9 max-w-[1400px] items-center gap-4 px-4 md:px-6">
          <span className="hidden shrink-0 items-center gap-1.5 text-xs font-medium text-primary-300 sm:flex">
            <i className="ri-notification-3-line text-[13px]" aria-hidden="true"></i>
            站点公告
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex w-max animate-marquee whitespace-nowrap text-xs text-background-200">
              <span className="pr-16">{noticeTicker}</span>
              <span className="pr-16">{noticeTicker}</span>
            </div>
          </div>
          <span className="hidden shrink-0 items-center gap-1.5 text-xs text-background-300 md:flex">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary-400"></span>
            线路检测正常
          </span>
        </div>
      </div>

      {/* main bar */}
      <div className="border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-4 px-4 md:h-[72px] md:px-6">
          <a href="#top" className="flex shrink-0 items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-lg font-bold text-foreground-950">
              撸
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-bold tracking-wide text-foreground-950">撸撸射</span>
              <span className="mt-0.5 text-[11px] tracking-[0.28em] text-foreground-500">入口导航</span>
            </span>
          </a>

          <nav className="hidden items-center gap-1 lg:flex">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-accent-700"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2 md:gap-3">
            <form onSubmit={handleSubmit} className="hidden items-center md:flex">
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2 transition-colors focus-within:border-primary-400">
                <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="text"
                  placeholder="搜索入口关键词"
                  className="w-36 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none lg:w-44"
                />
              </div>
            </form>

            <button
              type="button"
              onClick={handleGetEntry}
              className="hidden cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 sm:flex"
            >
              <i className="ri-flashlight-fill text-sm" aria-hidden="true"></i>
              获取最新入口
            </button>

            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label="打开菜单"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-2-line text-xl'} aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 lg:hidden">
            <div className="mx-auto max-w-[1400px] px-4 py-4 md:px-6">
              <div className="grid grid-cols-2 gap-2">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-md border border-background-200 bg-background-100 px-3 py-3 text-sm font-medium text-foreground-800"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  handleGetEntry();
                }}
                className="mt-3 w-full cursor-pointer rounded-md bg-accent-500 px-4 py-3 text-sm font-semibold text-background-50"
              >
                获取最新入口
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}